import { Box, Button } from '@mui/material';
import { Field, FieldProps, useFormikContext } from 'formik';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { PaloField, PaloFormGrid, PaloModal, paloPrintedPosition } from '@synergy/components-palo';
import { PoolButton, PoolNumberTextfield, PoolTextfield } from '@synergy/components-next';
import { PoolMarkdownEditor } from '@synergy/components';
import { ReactElement, ReactNode, useEffect, useRef } from 'react';
import { postProcessMarkdown } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';

import { FinancialCalculations } from '../../utils/financial-calculations';
import { PaloPrintCount } from './palo-print-count';
import { ProductSnapshot } from '../../models/product-snapshot';
import { bestLocaleSelector } from '../../selectors/locale-selectors';
import { getProductPrice } from '../../utils/get-product-price';
import { useAppSelector } from '../../../store/use-appstate-selector';

export interface PaloPositionEditModalProps {
    open: boolean;
    /** The row's place in the document, counted from zero. */
    index?: number;
    /** The Formik path of the positions; both documents call it `allItems`. */
    fieldName?: string;
    readonly?: boolean;
    currency: string;
    /** What the document has of its own, e.g. the invoice's counter account. */
    extraFields?: (prefix: string) => ReactNode;
    /** The handles of the row menu, so the row and the dialog offer the same. */
    onDuplicate?: () => void;
    onInsertAbove?: () => void;
    onInsertBelow?: () => void;
    onDelete?: () => void;
    onClose: () => void;
}

const HEADER_ACTION_SX = {
    minWidth: 0,
    padding: '2px 8px',
    fontSize: '11.5px',
    fontWeight: 500,
    color: tokens.color.ink2,
    textTransform: 'none',
};

const READ_VALUE_SX = { fontSize: '12.5px', color: tokens.color.ink2, padding: '7px 0' };

/**
 * Every field here carries its name above it, as the mask does. A control that
 * brings a label of its own would say it twice — the table hides it the same
 * way.
 */
const FIELDS_SX = {
    '& .MuiInputLabel-root': { display: 'none' },
    '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
};

/**
 * One position, opened from its row.
 *
 * ERPNext's pencil opens the whole row — what it is about, what it costs, the
 * description — with the handles of the row beside the title. Ours showed the
 * name and two description fields, so a price or a quantity could only be
 * changed in the table, and the description could only be reached through a
 * dialog that did not show what it belonged to (#2208).
 *
 * It writes into the document as the row does, not into a copy of it: the sum
 * above the mask follows along while one types, and the fields are the same
 * ones the row draws. "Abbrechen" puts the position back as it was opened.
 */
export const PaloPositionEditModal = ({
    open,
    index,
    fieldName = 'allItems',
    readonly = false,
    currency,
    extraFields,
    onDuplicate,
    onInsertAbove,
    onInsertBelow,
    onDelete,
    onClose,
}: PaloPositionEditModalProps): ReactElement | null => {
    const locale = useAppSelector(bestLocaleSelector);
    const form = useFormikContext<Record<string, ProductSnapshot[]>>();
    const opened = useRef<ProductSnapshot | null>(null);
    const prefix = `${fieldName}.${index}`;
    const position = undefined === index ? undefined : form.values[fieldName]?.[index];

    useEffect(() => {
        /*
         * What to put back on "Abbrechen", noted once when the dialog opens.
         *
         * The position itself, not a copy of it: every edit goes through
         * Formik, which writes a new object rather than changing this one, so
         * the reference stays the position as it was. A copy was worse than
         * useless — spreading the record left a plain object where a
         * ProductSnapshot had been, the form then read the two as different,
         * and cancelling a dialog that changed nothing marked the document as
         * unsaved (#2207 R-144).
         */
        opened.current = open && position ? position : null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, index]);

    if (!open || undefined === index || !position) {
        return null;
    }

    const set = (field: string, value: unknown): void => {
        form.setFieldValue(`${prefix}.${field}`, value);
    };

    // What the PDF will carry of this position: the short description where
    // there is one, the long one otherwise (FRM-Z, #2225).
    const printed = paloPrintedPosition(position.shortDescription?.[locale], position.longDescription?.[locale]);

    const handleQuantityChange = (value: number): void => {
        set('quantity', value);
        // A quantity that crosses a scale price carries the price with it, the
        // same way it does in the row.
        set('price', getProductPrice(position.price ?? 0, currency, value, position.product));
    };

    const handleCancel = (): void => {
        // Nothing written where nothing moved: the write alone is what the
        // form reads as a change.
        if (opened.current && opened.current !== position) {
            form.setFieldValue(prefix, opened.current);
        }

        onClose();
    };

    const title = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box component="span">
                <FormattedMessage
                    id="app.positions.editPosition"
                    values={{ value: index + 1 }}
                    defaultMessage="Position {value}"
                />
            </Box>
            {!readonly && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    {onInsertAbove && (
                        <Button size="small" sx={HEADER_ACTION_SX} onClick={onInsertAbove}>
                            <FormattedMessage id="app.positions.insertAbove" />
                        </Button>
                    )}
                    {onInsertBelow && (
                        <Button size="small" sx={HEADER_ACTION_SX} onClick={onInsertBelow}>
                            <FormattedMessage id="app.positions.insertBelow" />
                        </Button>
                    )}
                    {onDuplicate && (
                        <Button size="small" sx={HEADER_ACTION_SX} onClick={onDuplicate}>
                            <FormattedMessage id="app.common.duplicate" />
                        </Button>
                    )}
                    {onDelete && (
                        <Button size="small" sx={{ ...HEADER_ACTION_SX, color: tokens.color.crit }} onClick={onDelete}>
                            <FormattedMessage id="app.common.delete" />
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );

    return (
        <PaloModal
            isVisible={open}
            title={title}
            handleClose={handleCancel}
            width="md"
            themeContent={true}
            actionChildren={
                <>
                    <PoolButton variant="text" onClick={handleCancel}>
                        <FormattedMessage id="app.common.cancel" />
                    </PoolButton>
                    <PoolButton disabled={readonly} onClick={onClose}>
                        <FormattedMessage id="app.common.apply" defaultMessage="Übernehmen" />
                    </PoolButton>
                </>
            }
        >
            <Box sx={FIELDS_SX}>
                <PaloFormGrid columns={2}>
                    <PaloField label={<FormattedMessage id="app.label.productName" />} span={2} name={`${prefix}.name`}>
                        <PoolTextfield
                            value={position.name?.[locale] ?? ''}
                            onChange={event => set('name', { ...position.name, [locale]: event.target.value })}
                            fullWidth={true}
                            required={true}
                            readonly={readonly}
                        />
                    </PaloField>
                    <PaloField label={<FormattedMessage id="app.freePosition.productNumber" />}>
                        <Box sx={READ_VALUE_SX}>{position.productNumber ?? '—'}</Box>
                    </PaloField>
                    <PaloField label={<FormattedMessage id="app.freePosition.supplierAccount" />}>
                        <Box sx={READ_VALUE_SX}>{position.supplierAccountName ?? '—'}</Box>
                    </PaloField>
                    <PaloField label={<FormattedMessage id="app.table.column.quantity" />} name={`${prefix}.quantity`}>
                        <Field name={`${prefix}.quantity`}>
                            {({ field }: FieldProps<number>) => (
                                <PoolNumberTextfield
                                    {...field}
                                    onChange={event => handleQuantityChange(Number(event.target.value))}
                                    type="dense"
                                    readonly={readonly}
                                    suffix={position.orderUnit ? ` ${position.orderUnit}` : undefined}
                                    textAlign="right"
                                    fullWidth={true}
                                />
                            )}
                        </Field>
                    </PaloField>
                    <PaloField label={<FormattedMessage id="app.table.column.price" />} name={`${prefix}.price`}>
                        <Field name={`${prefix}.price`}>
                            {({ field }: FieldProps<number>) => (
                                <PoolNumberTextfield
                                    minDecimalScale={2}
                                    {...field}
                                    type="dense"
                                    readonly={readonly}
                                    currency={currency}
                                    locale={locale}
                                    textAlign="right"
                                    fullWidth={true}
                                />
                            )}
                        </Field>
                    </PaloField>
                    <PaloField label={<FormattedMessage id="app.table.column.discount" />} name={`${prefix}.discount`}>
                        <Field name={`${prefix}.discount`}>
                            {({ field }: FieldProps<number>) => (
                                <PoolNumberTextfield
                                    minDecimalScale={2}
                                    {...field}
                                    type="dense"
                                    readonly={readonly}
                                    suffix=" %"
                                    textAlign="right"
                                    fullWidth={true}
                                />
                            )}
                        </Field>
                    </PaloField>
                    {extraFields?.(prefix)}
                    <PaloField label={<FormattedMessage id="app.table.column.position" />}>
                        <Box sx={{ ...READ_VALUE_SX, color: tokens.color.ink, fontWeight: 600, textAlign: 'right' }}>
                            <FormattedNumber
                                currency={currency}
                                value={FinancialCalculations.getNetTotal({
                                    quantity: position.quantity ?? 0,
                                    price: position.price,
                                    discount: position.discount,
                                })}
                                style="currency"
                            />
                        </Box>
                    </PaloField>
                    <PaloField
                        label={<FormattedMessage id="app.invoices.detail.shortDescriptionLabel" />}
                        span={2}
                        name={`${prefix}.shortDescription`}
                    >
                        <PoolTextfield
                            value={position.shortDescription?.[locale] ?? ''}
                            onChange={event =>
                                set('shortDescription', { ...position.shortDescription, [locale]: event.target.value })
                            }
                            fullWidth={true}
                            multiline={true}
                            rows={3}
                            readonly={readonly}
                        />
                        {printed.isShort && <PaloPrintCount length={printed.length} />}
                    </PaloField>
                    <PaloField
                        label={<FormattedMessage id="app.common.productDescription" />}
                        span={2}
                        name={`${prefix}.longDescription`}
                    >
                        <PoolMarkdownEditor
                            value={position.longDescription?.[locale] ?? ''}
                            onChange={value =>
                                set('longDescription', {
                                    ...position.longDescription,
                                    [locale]: postProcessMarkdown(value),
                                })
                            }
                            readOnly={readonly}
                        />
                        {!printed.isShort && printed.length > 0 && <PaloPrintCount length={printed.length} />}
                    </PaloField>
                </PaloFormGrid>
            </Box>
        </PaloModal>
    );
};
